import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { useApi } from "../../hooks/useApi";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip as MuiTooltip } from "@mui/material";

// ── Constants ─────────────────────────────────────────────────────────────────
const GOLD = "#9c6a36";
const GOLD18 = "#9c6a3618";

const cardSx = {
  borderRadius: 2,
  border: "0.1px solid",
  borderColor: "customDivider",
  backgroundColor: "offWhite",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  height: "100%",
  boxSizing: "border-box",
};

const STATUS_COLORS = {
  confirmed: "#378ADD",
  completed: "#1D9E75",
  cancelled: "#E24B4A",
  no_show: "#EF9F27",
  pending: "#7F77DD",
};

const PERIODS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
const pct = (n, d) => (d === 0 ? "0%" : `${Math.round((n / d) * 100)}%`);
const calcDelta = (curr, prev) => {
  if (prev === 0 && curr === 0) return null;
  if (prev === 0) return null;
  const d = ((curr - prev) / Math.abs(prev)) * 100;
  return `${d >= 0 ? "+" : ""}${Math.round(d)}%`;
};

// ── Sort toggle ───────────────────────────────────────────────────────────────
const SortToggle = ({ value, onChange }) => (
  <Box sx={{ display: "flex", gap: 0.5 }}>
    {[
      { key: "count", label: "Appts" },
      { key: "revenue", label: "Revenue" },
    ].map(({ key, label }) => (
      <Box
        key={key}
        onClick={() => onChange(key)}
        sx={{
          px: 1.25,
          py: 0.25,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          border: "1px solid",
          borderColor: value === key ? "#1a1a1a" : "customDivider",
          backgroundColor: value === key ? "#1a1a1a" : "transparent",
          color: value === key ? "white" : "text.secondary",
          transition: "all 0.15s",
          userSelect: "none",
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, deltaVal, positiveIsGood = true }) => {
  const isPositive = deltaVal?.startsWith("+");
  const deltaColor = isPositive
    ? positiveIsGood
      ? "#1D9E75"
      : "#E24B4A"
    : positiveIsGood
      ? "#E24B4A"
      : "#1D9E75";

  return (
    <Box sx={{ ...cardSx, p: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: "uppercase",
          color: GOLD,
          mb: 1.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h3"
        sx={{ fontWeight: 700, letterSpacing: -0.5, mb: 0.25 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block" }}
        >
          {sub}
        </Typography>
      )}
      {deltaVal && (
        <Typography
          variant="caption"
          sx={{ color: deltaColor, fontWeight: 600, display: "block", mt: 0.5 }}
        >
          {deltaVal} vs prev period
        </Typography>
      )}
    </Box>
  );
};

// ── Custom tooltip ────────────────────────────────────────────────────────────
const RevenueTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: "offWhite",
        border: "1px solid #f0f0f0",
        borderRadius: 1.5,
        p: 1.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {fmt(payload[0].value)}
      </Typography>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export default function Analytics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();

  const [activePeriod, setActivePeriod] = useState(30);
  const [isCustom, setIsCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  );
  const [customTo, setCustomTo] = useState(dayjs().format("YYYY-MM-DD"));

  const [bookings, setBookings] = useState([]);
  const [prevBookings, setPrevBookings] = useState([]);
  const [historicClientIds, setHistoricClientIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [barberSort, setBarberSort] = useState("count");
  const [serviceSort, setServiceSort] = useState("count");

  const { dateFrom, dateTo } = useMemo(() => {
    if (isCustom) return { dateFrom: customFrom, dateTo: customTo };
    return {
      // activePeriod - 1: "7 days" = today plus the previous 6 (7 inclusive),
      // not 8. Keeps the window length equal to the label and to the chart days.
      dateFrom: dayjs().subtract(activePeriod - 1, "day").format("YYYY-MM-DD"),
      dateTo: dayjs().format("YYYY-MM-DD"),
    };
  }, [isCustom, activePeriod, customFrom, customTo]);

  const days = Math.max(dayjs(dateTo).diff(dayjs(dateFrom), "day") + 1, 1);
  const prevDateTo = dayjs(dateFrom).subtract(1, "day").format("YYYY-MM-DD");
  const prevDateFrom = dayjs(dateFrom)
    .subtract(days, "day")
    .format("YYYY-MM-DD");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [res, prevRes, historicRes] = await Promise.all([
          api(
            `/tenants/appointments.php?date_from=${dateFrom}&date_to=${dateTo}&refund_by_update=1`,
            { method: "GET" },
          ),
          api(
            `/tenants/appointments.php?date_from=${prevDateFrom}&date_to=${prevDateTo}&refund_by_update=1`,
            { method: "GET" },
          ),
          api(
            `/tenants/appointments.php?date_from=2000-01-01&date_to=${prevDateTo}`,
            { method: "GET" },
          ),
        ]);
        setBookings(res.data.bookings || []);
        setPrevBookings(prevRes.data.bookings || []);
        setHistoricClientIds(
          new Set(
            (historicRes.data.bookings || [])
              .map((b) => b.client?.user_id)
              .filter(Boolean),
          ),
        );
      } catch {
        setBookings([]);
        setPrevBookings([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dateFrom, dateTo]);

  // The lists are fetched with refund_by_update=1, which ALSO returns bookings
  // refunded during this period whose appointment falls OUTSIDE it. Those belong
  // only in the Refunds figure — every other metric must be restricted to
  // bookings whose appointment date actually falls within the period, or counts
  // and rates get inflated (and a booking can be counted in two periods).
  const inRange = (b, from, to) => {
    const d = (b.starts_at_local || b.starts_at || "").slice(0, 10);
    return d >= from && d <= to;
  };
  const periodBookings = bookings.filter((b) => inRange(b, dateFrom, dateTo));
  const prevPeriodBookings = prevBookings.filter((b) =>
    inRange(b, prevDateFrom, prevDateTo),
  );

  const isPaidTxn = (b) =>
    Number(b.amount_paid) > 0 &&
    !(b.status === "cancelled" && b.payment_status === "refunded");

  // ── Current period metrics ────────────────────────────────────────────────
  const totalAppointments = periodBookings.length;
  const completed = periodBookings.filter((b) => b.status === "completed").length;
  const cancelled = periodBookings.filter((b) => b.status === "cancelled").length;
  const noShows = periodBookings.filter((b) => b.status === "no_show").length;
  const confirmed = periodBookings.filter((b) => b.status === "confirmed").length;
  const pending = periodBookings.filter((b) => b.status === "pending").length;

  const totalSales = periodBookings
    .filter(isPaidTxn)
    .reduce((s, b) => s + Number(b.amount_paid || 0), 0);
  // Avg ticket divides by the number of paid transactions (same rows summed into
  // totalSales) — not completed-only, which would include no-show / forfeited
  // revenue in the numerator but not the denominator.
  const paidTxnCount = periodBookings.filter(isPaidTxn).length;

  const avgTicket = paidTxnCount > 0 ? totalSales / paidTxnCount : 0;
  const avgDailyRevenue = totalSales / days;

  // Refunds are attributed by refund date (updated_at), so they use the full
  // refund_by_update result set. Refunded amount = service price + tip (the
  // processing fee is non-refundable).
  const refundedBookings = bookings.filter(
    (b) => b.payment_status === "refunded",
  );
  const refundsTotal = refundedBookings.reduce(
    (s, b) => s + Number(b.price_charged || 0) + Number(b.tip_amount || 0),
    0,
  );

  const completionRate = pct(completed, completed + noShows + cancelled);
  const cancellationRate = pct(cancelled, totalAppointments);
  const noShowRate = pct(noShows, totalAppointments);

  // ── Previous period metrics ───────────────────────────────────────────────
  const prevTotalAppointments = prevPeriodBookings.length;
  const prevCompleted = prevPeriodBookings.filter(
    (b) => b.status === "completed",
  ).length;
  const prevCancelled = prevPeriodBookings.filter(
    (b) => b.status === "cancelled",
  ).length;
  const prevNoShows = prevPeriodBookings.filter(
    (b) => b.status === "no_show",
  ).length;
  const prevTotalSales = prevPeriodBookings
    .filter(isPaidTxn)
    .reduce((s, b) => s + Number(b.amount_paid || 0), 0);
  const prevPaidTxnCount = prevPeriodBookings.filter(isPaidTxn).length;
  const prevAvgTicket =
    prevPaidTxnCount > 0 ? prevTotalSales / prevPaidTxnCount : 0;

  const deltaAppts = calcDelta(totalAppointments, prevTotalAppointments);
  const deltaSales = calcDelta(totalSales, prevTotalSales);
  const deltaTicket = calcDelta(avgTicket, prevAvgTicket);
  const deltaCompletion = calcDelta(
    completed / Math.max(completed + noShows + cancelled, 1),
    prevCompleted / Math.max(prevCompleted + prevNoShows + prevCancelled, 1),
  );
  const deltaCancellation = calcDelta(
    cancelled / Math.max(totalAppointments, 1),
    prevCancelled / Math.max(prevTotalAppointments, 1),
  );
  const deltaNoShow = calcDelta(
    noShows / Math.max(totalAppointments, 1),
    prevNoShows / Math.max(prevTotalAppointments, 1),
  );

  // ── New vs returning clients ──────────────────────────────────────────────
  const uniqueClientIds = [
    ...new Set(periodBookings.map((b) => b.client?.user_id).filter(Boolean)),
  ];
  const newClientCount = uniqueClientIds.filter(
    (id) => !historicClientIds.has(id),
  ).length;
  const returningClientCount = uniqueClientIds.filter((id) =>
    historicClientIds.has(id),
  ).length;
  const clientData = [
    { name: "Returning", value: returningClientCount, color: GOLD },
    { name: "New", value: newClientCount, color: "#1D9E75" },
  ].filter((d) => d.value > 0);

  // ── Status breakdown ──────────────────────────────────────────────────────
  const statusData = [
    { name: "Completed", value: completed, color: STATUS_COLORS.completed },
    { name: "Confirmed", value: confirmed, color: STATUS_COLORS.confirmed },
    { name: "Pending", value: pending, color: STATUS_COLORS.pending },
    { name: "Cancelled", value: cancelled, color: STATUS_COLORS.cancelled },
    { name: "No-show", value: noShows, color: STATUS_COLORS.no_show },
  ].filter((d) => d.value > 0);

  // ── Daily revenue ─────────────────────────────────────────────────────────
  const dailyRevenue = useMemo(() => {
    const map = {};
    bookings
      .filter(
        (b) =>
          Number(b.amount_paid) > 0 &&
          !(b.status === "cancelled" && b.payment_status === "refunded"),
      )
      .forEach((b) => {
        const d = (b.starts_at_local || b.starts_at).slice(0, 10);
        map[d] = (map[d] || 0) + Number(b.amount_paid || 0);
      });

    const result = [];
    let cur = dayjs(dateFrom);
    const end = dayjs(dateTo);
    while (cur.isSame(end, "day") || cur.isBefore(end, "day")) {
      const key = cur.format("YYYY-MM-DD");
      result.push({ date: cur.format("MMM D"), revenue: map[key] || 0 });
      cur = cur.add(1, "day");
    }
    if (result.length > 60) {
      const weekly = [];
      for (let i = 0; i < result.length; i += 7) {
        const slice = result.slice(i, i + 7);
        weekly.push({
          date: slice[0].date,
          revenue: slice.reduce((s, d) => s + d.revenue, 0),
        });
      }
      return weekly;
    }
    return result;
  }, [bookings, dateFrom, dateTo, days]);

  // ── Barber performance ────────────────────────────────────────────────────
  const barberStats = useMemo(() => {
    const map = {};
    bookings
      .filter((b) => {
        const d = (b.starts_at_local || b.starts_at || "").slice(0, 10);
        return d >= dateFrom && d <= dateTo;
      })
      .forEach((b) => {
      const key = b.barber?.user_id;
      if (!key) return;
      if (!map[key])
        map[key] = {
          name: `${b.barber.first_name} ${b.barber.last_name}`,
          appts: 0,
          revenue: 0,
          completed: 0,
        };
      map[key].appts++;
      if (b.status === "completed") map[key].completed++;
      if (
        Number(b.amount_paid) > 0 &&
        !(b.status === "cancelled" && b.payment_status === "refunded")
      ) {
        map[key].revenue += Number(b.amount_paid || 0);
      }
    });
    return Object.values(map).sort((a, b) =>
      barberSort === "revenue"
        ? b.revenue - a.revenue
        : b.appts !== a.appts
          ? b.appts - a.appts
          : b.revenue - a.revenue,
    );
  }, [bookings, barberSort, dateFrom, dateTo]);

  const totalBarberRevenue = barberStats.reduce((s, b) => s + b.revenue, 0);
  const maxBarberCount = Math.max(...barberStats.map((b) => b.appts), 1);

  // ── Service stats ─────────────────────────────────────────────────────────
  const serviceStats = useMemo(() => {
    const map = {};
    bookings
      .filter((b) => {
        const d = (b.starts_at_local || b.starts_at || "").slice(0, 10);
        return d >= dateFrom && d <= dateTo && b.status !== "cancelled";
      })
      .forEach((b) => {
        const key = b.service?.name || "Unknown";
        if (!map[key]) map[key] = { name: key, bookings: 0, revenue: 0 };
        map[key].bookings++;
        map[key].revenue += Number(b.price_charged || 0);
      });
    return Object.values(map).sort((a, b) =>
      serviceSort === "revenue"
        ? b.revenue - a.revenue
        : b.bookings - a.bookings,
    );
  }, [bookings, serviceSort, dateFrom, dateTo]);

  const maxServiceCount = Math.max(...serviceStats.map((s) => s.bookings), 1);
  const maxServiceRevenue = Math.max(...serviceStats.map((s) => s.revenue), 1);

  // ── Day of week ───────────────────────────────────────────────────────────
  const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const dowStats = useMemo(() => {
    const map = Array(7)
      .fill(0)
      .map((_, i) => ({ day: DOW_NAMES[i], appts: 0 }));
    bookings
      .filter((b) => b.status !== "cancelled")
      .forEach((b) => {
        const d = dayjs(b.starts_at_local || b.starts_at).day();
        map[d].appts++;
      });
    return map;
  }, [bookings]);
  const maxDow = Math.max(...dowStats.map((d) => d.appts), 1);

  // ── Busiest hours ─────────────────────────────────────────────────────────
  const hourStats = useMemo(() => {
    const map = {};
    bookings
      .filter((b) => b.status !== "cancelled")
      .forEach((b) => {
        const h = dayjs(b.starts_at_local || b.starts_at).hour();
        map[h] = (map[h] || 0) + 1;
      });
    return Array.from({ length: 14 }, (_, i) => i + 7).map((h) => ({
      hour: h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`,
      appts: map[h] || 0,
    }));
  }, [bookings]);

  const labelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: GOLD,
    display: "block",
    mb: 2,
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, py: 5 }}>
      {/* ── Header + period selector ─────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h4" : "h3"} sx={{ mb: 0.25 }}>
            Analytics
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Performance insights for your shop.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          {PERIODS.map(({ label, days: d }) => {
            const active = !isCustom && activePeriod === d;
            return (
              <Box
                key={d}
                component="button"
                onTouchEnd={(e) => { e.preventDefault(); setActivePeriod(d); setIsCustom(false); }}
                onClick={() => { setActivePeriod(d); setIsCustom(false); }}
                sx={{
                  backgroundColor: active ? "#1a1a1a" : "white",
                  color: active ? "white" : "#1a1a1a",
                  border: "1px solid",
                  borderColor: active ? "#1a1a1a" : "customDivider",
                  fontWeight: 500,
                  fontSize: 13,
                  height: 32,
                  minWidth: 0,
                  px: 1.5,
                  borderRadius: "16px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "@media (hover: hover)": {
                    "&:hover": { backgroundColor: active ? "#000" : "#f5f5f5" },
                  },
                }}
              >
                {label}
              </Box>
            );
          })}
          <Box
            component="button"
            onTouchEnd={(e) => { e.preventDefault(); setIsCustom(true); }}
            onClick={() => setIsCustom(true)}
            sx={{
              backgroundColor: isCustom ? "#1a1a1a" : "white",
              color: isCustom ? "white" : "#1a1a1a",
              border: "1px solid",
              borderColor: isCustom ? "#1a1a1a" : "customDivider",
              fontWeight: 500,
              fontSize: 13,
              height: 32,
              minWidth: 0,
              px: 1.5,
              borderRadius: "16px",
              cursor: "pointer",
              fontFamily: "inherit",
              "@media (hover: hover)": {
                "&:hover": { backgroundColor: isCustom ? "#000" : "#f5f5f5" },
              },
            }}
          >
            Custom
          </Box>
          {isCustom && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                type="date"
                size="small"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>to</Typography>
              <TextField
                type="date"
                size="small"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                slotProps={{ htmlInput: { min: customFrom } }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 8 }}>
          <CircularProgress size={18} sx={{ color: "text.secondary" }} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading analytics...
          </Typography>
        </Box>
      ) : (
        <>
          {/* ── KPI cards ──────────────────────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
              mb: 4,
            }}
          >
            <StatCard
              label="Total Appointments"
              value={totalAppointments}
              sub={`over ${days} days`}
              deltaVal={deltaAppts}
            />
            <StatCard
              label="Total Sales"
              value={fmt(totalSales)}
              sub={`${fmt(avgDailyRevenue)}/day avg`}
              deltaVal={deltaSales}
            />
            <StatCard
              label="Avg Ticket"
              value={fmt(avgTicket)}
              sub="per completed appt"
              deltaVal={deltaTicket}
            />
            <StatCard
              label="Completion Rate"
              value={completionRate}
              sub={`${completed} completed`}
              deltaVal={deltaCompletion}
            />
            <StatCard
              label="Cancellation Rate"
              value={cancellationRate}
              sub={`${cancelled} cancelled`}
              deltaVal={deltaCancellation}
              positiveIsGood={false}
            />
            <StatCard
              label="No-show Rate"
              value={noShowRate}
              sub={`${noShows} no-shows`}
              deltaVal={deltaNoShow}
              positiveIsGood={false}
            />
          </Box>

          {/* ── Revenue chart + Status pie ──────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Revenue Over Time</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={dailyRevenue}
                  margin={{ top: 0, right: 0, left: -10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#9b9b9b" }}
                    tickLine={false}
                    axisLine={false}
                    interval={
                      dailyRevenue.length > 30
                        ? Math.floor(dailyRevenue.length / 10)
                        : "preserveStartEnd"
                    }
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9b9b9b" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) =>
                      v === 0 ? "$0" : `$${(v / 1000).toFixed(0)}k`
                    }
                  />
                  <Tooltip
                    content={<RevenueTooltip />}
                    cursor={{ fill: GOLD18 }}
                  />
                  <Bar dataKey="revenue" fill={GOLD} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Appointment Status</Typography>
              {statusData.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No data for this period.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {statusData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [`${v} appts`, name]} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      formatter={(v) => (
                        <span style={{ fontSize: 12, color: "#4b4b4b" }}>
                          {v}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>

          {/* ── New vs Returning + Refunds ──────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
              gap: 3,
              mb: 4,
            }}
          >
            {/* New vs Returning */}
            <Box sx={{ ...cardSx, p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography sx={{ ...labelSx, mb: 0 }}>
                  New vs. Returning Clients
                </Typography>
                <MuiTooltip
                  title="A client is counted as 'new' if this is their first ever booking at your shop. A client is 'returning' if they had at least one booking before the selected date range. If all your bookings fall within the selected period, everyone will appear as new."
                  placement="top"
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: "text.disabled",
                      cursor: "pointer",
                    }}
                  />
                </MuiTooltip>
              </Box>
              {uniqueClientIds.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No data for this period.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <ResponsiveContainer width={160} height={160}>
                      <PieChart>
                        <Pie
                          data={clientData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          dataKey="value"
                          paddingAngle={2}
                        >
                          {clientData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v, name) => [`${v} clients`, name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    <Box>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 700, color: "#1D9E75" }}
                      >
                        {newClientCount}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        New clients
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {pct(newClientCount, uniqueClientIds.length)} of total
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 700, color: GOLD }}
                      >
                        {returningClientCount}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        Returning clients
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {pct(returningClientCount, uniqueClientIds.length)} of
                        total
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Refunds */}
            <Box sx={{ ...cardSx, p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography sx={{ ...labelSx, mb: 0 }}>Refunds</Typography>
                <MuiTooltip
                  title="Total amount refunded to clients during the selected period. Only bookings where the payment was fully refunded are counted here."
                  placement="top"
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={6000}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: "text.disabled",
                      cursor: "pointer",
                    }}
                  />
                </MuiTooltip>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  letterSpacing: -0.5,
                  mb: 0.25,
                  color: refundsTotal > 0 ? "#E24B4A" : "text.primary",
                }}
              >
                {refundsTotal > 0 ? `-${fmt(refundsTotal)}` : fmt(0)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                {refundedBookings.length} refunded booking
                {refundedBookings.length !== 1 ? "s" : ""} this period
              </Typography>
            </Box>
          </Box>

          {/* ── Barber performance + Service breakdown ──────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ ...cardSx, overflow: "hidden" }}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                  Barber Performance
                </Typography>
                <SortToggle value={barberSort} onChange={setBarberSort} />
              </Box>
              <Box sx={{ px: 3, py: 2 }}>
                {barberStats.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No data for this period.
                  </Typography>
                ) : (
                  barberStats.map((b, i) => (
                    <Box
                      key={b.name}
                      sx={{ mb: i < barberStats.length - 1 ? 2.5 : 0 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {b.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {barberSort === "revenue"
                            ? fmt(b.revenue)
                            : `${b.appts} appt${b.appts !== 1 ? "s" : ""}`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#f0efec",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.round(((barberSort === "revenue" ? b.revenue : b.appts) / (barberSort === "revenue" ? barberStats[0].revenue : maxBarberCount)) * 100)}%`,
                              backgroundColor: GOLD,
                              borderRadius: 3,
                              transition: "width 0.6s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            minWidth: 32,
                            textAlign: "right",
                          }}
                        >
                          {barberSort === "revenue"
                            ? pct(b.revenue, totalBarberRevenue)
                            : pct(b.appts, totalAppointments)}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>

            <Box sx={{ ...cardSx, overflow: "hidden" }}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                  Top Services
                </Typography>
                <SortToggle value={serviceSort} onChange={setServiceSort} />
              </Box>
              <Box sx={{ px: 3, py: 2 }}>
                {serviceStats.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No data for this period.
                  </Typography>
                ) : (
                  serviceStats.map((s, i) => (
                    <Box
                      key={s.name}
                      sx={{ mb: i < serviceStats.length - 1 ? 2.5 : 0 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          noWrap
                        >
                          {s.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", flexShrink: 0, ml: 1 }}
                        >
                          {serviceSort === "revenue"
                            ? fmt(s.revenue)
                            : `${s.bookings} appt${s.bookings !== 1 ? "s" : ""}`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: "#f0efec",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${Math.round(((serviceSort === "revenue" ? s.revenue : s.bookings) / (serviceSort === "revenue" ? maxServiceRevenue : maxServiceCount)) * 100)}%`,
                            backgroundColor: "#b0a898",
                            borderRadius: 3,
                            transition: "width 0.6s ease",
                          }}
                        />
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>

          {/* ── Busiest days + Busiest hours ─────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 3,
            }}
          >
            {/* Busiest days of week */}
            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Busiest Days of the Week</Typography>
              <Box sx={{ overflowX: "auto", mx: -1 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: 1.5,
                    minWidth: 280,
                    px: 1,
                  }}
                >
                  {dowStats.map((d) => (
                    <Box key={d.day} sx={{ textAlign: "center" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                          fontSize: 10,
                          display: "block",
                          mb: 1,
                        }}
                      >
                        {d.day}
                      </Typography>
                      <Box
                        sx={{
                          height: 80,
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          mb: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: "60%",
                            height: `${Math.max((d.appts / maxDow) * 100, 4)}%`,
                            backgroundColor:
                              d.appts === maxDow ? GOLD : "#f0efec",
                            borderRadius: "3px 3px 0 0",
                            transition: "height 0.4s ease",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: d.appts === maxDow ? GOLD : "text.primary",
                        }}
                      >
                        {d.appts}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Busiest hours of day */}
            <Box sx={{ ...cardSx, p: 3 }}>
              <Typography sx={labelSx}>Busiest Hours of the Day</Typography>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart
                  data={hourStats}
                  margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
                >
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 10, fill: "#9b9b9b" }}
                    tickLine={false}
                    axisLine={false}
                    interval={1}
                  />
                  <YAxis hide />
                  <Tooltip
                    formatter={(v) => [`${v} appts`, "Appointments"]}
                    cursor={{ fill: GOLD18 }}
                  />
                  <Bar dataKey="appts" fill={GOLD} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
